import * as Tabs from '@radix-ui/react-tabs';
import { Search } from '@styled-icons/material-outlined';
import { TEMPLATE_CATEGORY, TEMPLATE_TYPE } from '@typedream/data';
import { BaseDreamEditor } from '@typedream/dream-editor/src/types';
import { useEffect, useMemo, useState } from 'react';

import { useSpaceIDFromPath } from '@/data/space/hooks';
import { DreamEditor } from '@/sharedComponents/TextEditor/plate';
import { useListSectionTemplatesQuery, useListTemplatesQuery } from '@/store';
import { PageTemplateType, SectionTemplateType } from '@/types';

import useCollectFilterPage from '@/utils/useGetTemplateAndFilter';
import classNames from 'classnames';
import { PageTemplatesContent } from './PageTemplatesContent';
import { SectionTemplatesContent } from './SectionTemplatesContent';

function fmtCategory(category: string): string {
  const cat = category.replaceAll('_', ' ');
  return cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
}

export function TemplatesTabContent({
  editor,
  resetSidebarTab,
}: {
  editor: DreamEditor | BaseDreamEditor;
  resetSidebarTab: () => void;
}) {
  const [tab, settab] = useState('page');
  const [templates, settemplates] = useState([]);
  const [searchValue, setsearchValue] = useState('');
  const [byteam, setbyteam] = useState(false);
  const [pageCategory, setpageCategory] = useState('ALL');
  const [sectionCategory, setsectionCategory] = useState(TEMPLATE_CATEGORY.HERO);
  const spaceID = useSpaceIDFromPath();

  const {
    data: PAGE_TEMPLATES,
    isLoading: isLoadingPageTemplates,
    error: errorPageTemplates,
  } = useListTemplatesQuery([
    spaceID,
    byteam,
    {
      category: pageCategory !== 'ALL' ? pageCategory : '',
    },
  ]);

  const {
    data: SECTION_TEMPLATES,
    isLoading: isLoadingSectionTemplates,
    error: errorSectionTemplates,
  } = useListSectionTemplatesQuery();

  const { filters: filterCategories } = useCollectFilterPage({});
  const categories = useMemo(() => {
    if (tab === 'page') {
      if (!filterCategories) return [];
      const { pageCategories } = filterCategories;
      const catArr = pageCategories?.map((c) => c.key);
      return ['By Team', 'ALL', ...catArr];
    }
    if (tab === 'section') {
      if (!SECTION_TEMPLATES) return [];
      return Object.keys(SECTION_TEMPLATES);
    }
    return [];
  }, [SECTION_TEMPLATES, tab, filterCategories]);

  useEffect(() => {
    if (tab === 'page') {
      if (PAGE_TEMPLATES) {
        settemplates(
          PAGE_TEMPLATES.filter((template: PageTemplateType | SectionTemplateType) =>
            [TEMPLATE_TYPE.PAGE, TEMPLATE_TYPE.BLOG_PAGE].includes(template.type)
          )
        );
      }
    } else if (tab === 'section') {
      if (SECTION_TEMPLATES) {
        settemplates(
          Object.entries(SECTION_TEMPLATES[sectionCategory].templates).map(([key, value]) => ({
            id: key,
            ...value,
          }))
        );
      }
    }
  }, [PAGE_TEMPLATES, SECTION_TEMPLATES, pageCategory, sectionCategory, spaceID, tab]);

  const activeCategory = useMemo(() => {
    if (tab === 'page') {
      if (byteam) return 'By Team';
      return pageCategory;
    }
    return sectionCategory;
  }, [byteam, pageCategory, sectionCategory, tab]);

  const filteredTemplates = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (search) {
      setpageCategory('ALL');
      setsectionCategory(TEMPLATE_CATEGORY.HERO);
      switch (tab) {
        case 'page':
          return templates.filter(
            (t: PageTemplateType) =>
              (t.name || '').toLowerCase().includes(search) ||
              (t.description || '').toLowerCase().includes(search) ||
              (t.category || []).some((cat) => cat.toLowerCase().includes(search))
          );
        case 'section': {
          const sectionTemplates = Object.entries(SECTION_TEMPLATES).reduce(
            (acc, [key, value]) => [
              ...acc,
              ...Object.entries(value.templates).map(([name, t]) => ({
                ...t,
                name: name.replaceAll('_', ' ').toLowerCase(),
                key: key.replaceAll('_', ' ').toLowerCase(),
              })),
            ],
            []
          );

          return sectionTemplates.filter(
            (t) =>
              t.name.includes(search) ||
              (t.description || '').toLowerCase().includes(search) ||
              t.key.includes(search) ||
              (t.category || []).some((cat) => cat.toLowerCase().includes(search))
          );
        }
        default:
          break;
      }
    }
    return templates;
  }, [SECTION_TEMPLATES, searchValue, tab, templates]);

  return (
    <div className="pt-5 px-0 h-full overflow-hidden">
      <div className="relative mb-4">
        <Search
          size={20}
          className="absolute left-[16px] translate-y-1/2 color-[var(--deprecated-neutral-600)]"
        />
        <input
          className={classNames(
            'w-full !min-w-full py-2.5 !pl-10 !pr-4 shadow-[var(--shadow-xs)] text-sm font-medium',
            'bg-[var(--background-input)] border border-solid border-[var(--border-input)] rounded-lg',
            'placeholder-[var(--deprecated-neutral-600)]',
            'disabled:pointer-events-none disabled:bg-[var(--deprecated-neutral-100)]',
            'text-[13px] h-[40px]'
          )}
          value={searchValue}
          onChange={(e) => setsearchValue(e.target.value)}
          type="search"
          placeholder="Search templates"
          disabled={isLoadingPageTemplates || isLoadingSectionTemplates}
        />
      </div>
      <Tabs.Root
        className=" overflow-hidden"
        style={{
          height: 'calc(100% - 40px)',
        }}
        value={tab}
        onValueChange={(value) => {
          settab(value);
          setbyteam(false);
          setpageCategory('ALL');
          setsectionCategory(TEMPLATE_CATEGORY.HERO);
          setsearchValue('');
        }}
      >
        <Tabs.List className="flex mb-3">
          {['Page', 'Section'].map((section) => (
            <Tabs.Trigger
              key={section}
              value={section.toLowerCase()}
              className={classNames(
                'flex-1 h-[40px] border-0 bg-transparent border-b-2 border-solid border-transparent',
                'font-semibold text-sm leading-[20px] text-[var(--deprecated-neutral-600)] cursor-pointer',
                'disabled:opacity-60 disabled:pointer-events-none disabled:cursor-default',
                'aria-selected:border-[var(--accent)] aria-selected:text-[var(--deprecated-neutral-800)]',
                'transition'
              )}
            >
              {section}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="flex gap-1 max-w-full overflow-auto">
          {categories.map((cat) => (
            <button
              type="button"
              key={cat}
              className={classNames(
                'flex py-1 px-2 text-[13px] leading-5 whitespace-nowrap bg-none border-none bg-transparent transition uppercase',
                'disabled:opacity-60 disabled:pointer-events-none disabled:cursor-default',
                cat === activeCategory
                  ? 'font-semibold text-[var(--accent)]'
                  : 'font-medium text-neutral-600 hover:text-neutral-400 cursor-pointer'
              )}
              onClick={() => {
                setsearchValue('');
                if (tab === 'page') {
                  if (cat === 'By Team') {
                    setbyteam(true);
                    setpageCategory('ALL');
                    return;
                  }
                  setpageCategory(cat);
                  setbyteam(false);
                  return;
                }
                setsectionCategory(cat as TEMPLATE_CATEGORY);
              }}
            >
              {fmtCategory(cat)}
            </button>
          ))}
        </div>

        <Tabs.Content
          className="overflow-auto"
          value="page"
          style={{
            height: 'calc(100% - 110px)',
          }}
        >
          <PageTemplatesContent
            templates={filteredTemplates}
            loading={isLoadingPageTemplates}
            err={errorPageTemplates}
            editor={editor}
            resetTab={resetSidebarTab}
          />
        </Tabs.Content>
        <Tabs.Content
          className="overflow-auto"
          value="section"
          style={{
            height: 'calc(100% - 110px)',
          }}
        >
          <SectionTemplatesContent
            templates={filteredTemplates}
            loading={isLoadingSectionTemplates}
            err={errorSectionTemplates}
            editor={editor}
            sectionCategory={sectionCategory}
            resetTab={resetSidebarTab}
          />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
